<template>
    <div class="autocomplete-input form-group position-relative">
        <label v-if="label" :for="id">{{ label }}</label>

        <input
            :id="id"
            ref="input"
            aria-autocomplete="list"
            :aria-expanded="dropdownVisible ? 'true' : 'false'"
            :aria-invalid="Boolean(errorToShow)"
            :autocomplete="browserAutocomplete"
            class="form-control"
            :disabled="disabled"
            :value="displayValue"
            @blur="onBlur"
            @focus="onFocus"
            @input="onInput"
            @keydown.down.prevent="onArrowDown"
            @keydown.enter.prevent="onEnter"
            @keydown.esc.prevent="closeList"
            @keydown.up.prevent="onArrowUp">

        <div
            v-if="dropdownVisible"
            class="list-group position-absolute w-100 shadow-sm"
            style="z-index: 1000;">
            <div v-if="!pending && !loading">
                <div
                    v-for="(optionItem, optionIndex) in options"
                    :key="optionItem.value + ':' + optionIndex"
                    class="list-group-item list-group-item-action"
                    :class="{ active: optionIndex === activeIndex }"
                    @mousedown.prevent="choose(optionItem)">
                    {{ optionItem.text }}
                </div>
            </div>

            <div v-if="(pending || loading)" class="list-group-item text-muted">
                Lädt ...
            </div>

            <div v-else-if="searched && options.length === 0" class="list-group-item text-muted">
                Keine Übereinstimmungen gefunden.
            </div>
        </div>

        <div v-if="errorToShow" class="text-danger mt-1">
            {{ errorToShow }}
        </div>
    </div>
</template>

<script>
    import '@desktop/scss/components/autocomplete-input.scss';

    export default {
        name: 'AutocompleteInput',
        props: {
            id: {type: String, required: true},
            label: {type: String, default: ''},

            value: {type: String, default: ''}, // v-model string
            disabled: {type: Boolean, default: false},

            minLength: {type: Number, default: 3},
            delay: {type: Number, default: 500},

            fetchUrl: {type: String, required: true}, // must end with term=
            mapResults: {type: Function, required: true}, // json -> [{value,text,raw}]

            normalize: {type: Function, default: function(value) { return value; }},
            validate: {type: Function, default: function() { return null; }},

            browserAutocomplete: {type: String, default: 'off'},
        },
        emits: ['input', 'selected'],
        data: function() {
            return {
                loading: false,
                pending: false, // debounce window
                searched: false, // set true after request completed

                options: [],
                open: false,
                activeIndex: -1,

                timer: null,
                lastRequestedTerm: '',

                error: null,

                // while user types we display the typed text, not the last parent value
                draftValue: null,
            };
        },
        computed: {
            displayValue: function() {
                return this.draftValue !== null ? this.draftValue : (this.value || '');
            },
            dropdownVisible: function() {
                return this.open && (this.pending || this.loading || this.options.length > 0 || this.searched);
            },
            errorToShow: function() {
                if (this.open) return null;
                if (this.pending || this.loading) return null;
                return this.error;
            },
        },
        watch: {
            value: function() {
                this.draftValue = null;
            },
        },
        methods: {
            onInput: function(event) {
                var rawValue = event.target.value;
                var term = this.normalize(rawValue || '');
                this.draftValue = term;
                this.$emit('input', term);

                if (term.length < this.minLength) {
                    clearTimeout(this.timer);
                    this.pending = false;
                    this.loading = false;
                    this.searched = false;
                    this.options = [];
                    this.error = null;
                    this.closeList();
                    return;
                }

                this.scheduleFetch(term, {immediate: false});
            },

            onFocus: function() {
                if (this.disabled) return;

                var term = this.normalize(this.displayValue || '');
                if (term.length < this.minLength) return;

                if (this.options.length > 0 || this.searched) {
                    this.openList();
                }
            },

            onBlur: function() {
                var self = this;
                window.setTimeout(function() {
                    self.closeList();

                    var term = self.normalize(self.displayValue || '');
                    if (term.length < self.minLength) {
                        self.error = null;
                    } else {
                        self.error = self.validate(term);
                    }

                    self.draftValue = null;
                }, 100);
            },

            onArrowDown: function() {
                if (!this.open) this.openList();
                if (this.options.length === 0) return;
                this.activeIndex = Math.min(this.activeIndex + 1, this.options.length - 1);
            },

            onArrowUp: function() {
                if (!this.open) this.openList();
                if (this.options.length === 0) return;
                this.activeIndex = Math.max(this.activeIndex - 1, 0);
            },

            onEnter: function() {
                if (!this.open) return;
                if (this.activeIndex < 0 || this.activeIndex >= this.options.length) {
                    this.closeList();
                    return;
                }
                this.choose(this.options[this.activeIndex]);
            },

            openList: function() {
                if (this.disabled) return;
                this.open = true;
            },

            closeList: function() {
                this.open = false;
                this.activeIndex = -1;
            },

            choose: function(optionItem) {
                if (!optionItem) return;

                var selectedValue = optionItem.value || '';
                this.draftValue = selectedValue;
                this.$emit('input', selectedValue);

                if (optionItem.raw) this.$emit('selected', optionItem.raw);

                this.error = this.validate(selectedValue);
                this.closeList();

                if (this.$refs.input) this.$refs.input.blur();
            },

            scheduleFetch: function(term, options) {
                if (this.disabled) return;

                var scheduleOptions = options || {};
                var immediate = Boolean(scheduleOptions.immediate);

                var normalizedTerm = this.normalize(term || '');
                if (normalizedTerm.length < this.minLength) {
                    this.options = [];
                    this.closeList();
                    this.pending = false;
                    this.loading = false;
                    this.searched = false;
                    return;
                }

                this.openList();
                clearTimeout(this.timer);

                this.pending = true;

                var self = this;
                var run = function() {
                    self.fetchOptions(normalizedTerm);
                };

                if (immediate || this.delay <= 0) {
                    run();
                    return;
                }

                this.timer = setTimeout(run, this.delay);
            },

            fetchOptions: async function(term) {
                this.lastRequestedTerm = term;
                this.loading = true;

                try {
                    var response = await fetch(this.fetchUrl + encodeURIComponent(term), {
                        headers: {Accept: 'application/json'},
                    });
                    var json = await response.json();

                    var rows = Array.isArray(json) ? json : (json.data || []);
                    var mapped = this.mapResults(rows) || [];

                    // ignore out-of-order results
                    if (term !== this.lastRequestedTerm) return;

                    this.options = mapped;
                    this.activeIndex = mapped.length > 0 ? 0 : -1;
                    this.searched = true;
                } catch (error) {
                    if (term === this.lastRequestedTerm) {
                        this.options = [];
                        this.searched = true;
                    }
                } finally {
                    if (term === this.lastRequestedTerm) {
                        this.loading = false;
                        this.pending = false;
                    }
                }
            },
        },
    };
</script>
